import React, { useState, useEffect, useCallback } from 'react';
import { 
    Container, Row, Col, Card, Button, Modal, Form, 
    Alert, Spinner, Dropdown, ButtonGroup, InputGroup, 
    Badge, Tab, Nav, Table
} from 'react-bootstrap';
import { 
    FaFolder, FaFolderOpen, FaPlus, FaEdit, FaTrash, 
    FaSearch, FaSort, FaEllipsisV, FaFileAlt, 
    FaArrowUp, FaArrowDown, FaSave, FaTimes,
    FaChartBar, FaBuilding, FaFilter,
    FaSync, FaArrowsAlt, FaLayerGroup, FaArchive
} from 'react-icons/fa';
import toast from 'react-hot-toast';

import dynamicFormsService from '../../services/dynamicFormsService';
import FolderTreeView from './components/FolderTreeView';
import FormAssignmentWizard from './components/FormAssignmentWizard';
import FolderStatistics from './components/FolderStatistics';
import '../componentCss/healthcare-modern.css';
import './styles/departmentFormsList.css';

const DepartmentFormsList = () => {
    // Core data states
    const [folders, setFolders] = useState([]);
    const [forms, setForms] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // UI states
    const [activeTab, setActiveTab] = useState('overview');
    const [viewMode, setViewMode] = useState('tree'); // 'tree', 'list', 'grid'
    const [expandedFolders, setExpandedFolders] = useState(new Set());

    // Modal states
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showEditFolderModal, setShowEditFolderModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAssignmentWizard, setShowAssignmentWizard] = useState(false);

    // Form states
    const [folderForm, setFolderForm] = useState({
        name: '',
        description: '',
        department_id: '', // Always use empty string for consistency
        parent_folder_id: null,
        is_active: true
    });
    const [selectedFolder, setSelectedFolder] = useState(null);

    // Filter and search states
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [filterDepartment, setFilterDepartment] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Statistics
    const [statistics, setStatistics] = useState({
        totalFolders: 0,
        totalForms: 0,
        formsWithoutFolder: 0,
        departmentBreakdown: {},
        recentActivity: []
    });

    useEffect(() => {
        initializeData();
    }, []);

    const initializeData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadFolders(),
                loadForms(),
                loadDepartments()
            ]);
            calculateStatistics();
        } catch (error) {
            console.error('Error initializing data:', error);
            setError('Failed to load data. Please refresh the page.');
        } finally {
            setLoading(false);
        }
    };

    const loadFolders = async () => {
        try {
            const response = await dynamicFormsService.getFolders();
            if (response.success) {
                setFolders(response.data || []);
            } else {
                throw new Error(response.message || 'Failed to load folders');
            }
        } catch (error) {
            console.error('Error loading folders:', error);
            toast.error('Failed to load folders');
        }
    };

    const loadForms = async () => {
        try {
            const response = await dynamicFormsService.getForms();
            if (response.success) {
                setForms(response.data || []);
            } else {
                throw new Error(response.message || 'Failed to load forms');
            }
        } catch (error) {
            console.error('Error loading forms:', error);
            toast.error('Failed to load forms');
        }
    };

    const loadDepartments = async () => {
        try {
            const response = await dynamicFormsService.getDepartments();
            if (response.success) {
                setDepartments(response.data || []);
            } else {
                console.warn('Departments not available:', response.message);
                setDepartments([]);
            }
        } catch (error) {
            console.error('Error loading departments:', error);
            setDepartments([]);
        }
    };

    const calculateStatistics = useCallback(() => {
        const stats = {
            totalFolders: folders.length,
            totalForms: forms.length,
            formsWithoutFolder: forms.filter(form => !form.folder_id).length,
            departmentBreakdown: {},
            recentActivity: []
        };

        // Calculate department breakdown
        departments.forEach(dept => {
            const deptFolders = folders.filter(folder => folder.department_id === dept.department_id);
            const deptForms = forms.filter(form => {
                const formFolder = folders.find(folder => folder.folder_id === form.folder_id);
                return formFolder && formFolder.department_id === dept.department_id;
            });
            
            stats.departmentBreakdown[dept.name] = {
                folders: deptFolders.length,
                forms: deptForms.length
            };
        });

        setStatistics(stats);
    }, [folders, forms, departments]);

    useEffect(() => {
        calculateStatistics();
    }, [calculateStatistics]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await Promise.all([
                loadFolders(),
                loadForms(),
                loadDepartments()
            ]);
            toast.success('Data refreshed successfully');
        } catch (error) {
            toast.error('Failed to refresh data');
        } finally {
            setRefreshing(false);
        }
    };

    const handleCreateFolder = async () => {
        if (!folderForm.name.trim()) {
            toast.error('Folder name is required');
            return;
        }

        try {
            const response = await dynamicFormsService.createFolder({
                ...folderForm,
                department_id: folderForm.department_id || null
            });
            
            if (response.success) {
                toast.success('Folder created successfully');
                setShowCreateFolderModal(false);
                resetFolderForm();
                await loadFolders();
            } else {
                toast.error(response.message || 'Failed to create folder');
            }
        } catch (error) {
            console.error('Error creating folder:', error);
            toast.error('Error creating folder');
        }
    };

    const handleEditFolder = async () => {
        if (!folderForm.name.trim()) {
            toast.error('Folder name is required');
            return;
        }

        try {
            // Prepare payload with proper handling for department_id
            const payload = {
                ...folderForm,
                department_id: folderForm.department_id || null // Convert empty string to null for API
            };

            console.log('Edit folder payload:', payload);
            console.log('Selected folder original department_id:', selectedFolder.department_id);
            console.log('Current folderForm state:', folderForm);

            const response = await dynamicFormsService.updateFolder(
                selectedFolder.folder_id, 
                payload
            );
            
            if (response.success) {
                toast.success('Folder updated successfully');
                setShowEditFolderModal(false);
                setSelectedFolder(null);
                resetFolderForm();
                await loadFolders();
            } else {
                toast.error(response.message || 'Failed to update folder');
            }
        } catch (error) {
            console.error('Error updating folder:', error);
            toast.error('Error updating folder');
        }
    };

    const handleDeleteFolder = async () => {
        if (!selectedFolder) return;

        try {
            const response = await dynamicFormsService.deleteFolder(selectedFolder.folder_id);
            
            if (response.success) {
                toast.success('Folder deleted successfully');
                setShowDeleteModal(false);
                setSelectedFolder(null);
                await loadFolders();
            } else {
                toast.error(response.message || 'Failed to delete folder');
            }
        } catch (error) {
            console.error('Error deleting folder:', error);
            toast.error('Error deleting folder');
        }
    };

    const resetFolderForm = () => {
        setFolderForm({
            name: '',
            description: '',
            department_id: '', // Consistent empty string
            parent_folder_id: null,
            is_active: true
        });
    };

    const openCreateFolderModal = () => {
        resetFolderForm();
        setShowCreateFolderModal(true);
    };

    const openEditFolderModal = (folder) => {
        console.log('📂 Opening edit modal for folder:', folder);
        setSelectedFolder(folder);
        
        // Normalize department_id to string for consistent handling
        const normalizedDepartmentId = folder.department_id ? String(folder.department_id) : '';
        
        setFolderForm({
            name: folder.name,
            description: folder.description || '',
            department_id: normalizedDepartmentId, // ✅ Always string
            parent_folder_id: folder.parent_folder_id,
            is_active: folder.is_active !== undefined ? folder.is_active : true
        });
        
        console.log('📝 Initial form state set with department_id:', normalizedDepartmentId);
        setShowEditFolderModal(true);
    };

    const openDeleteModal = (folder) => {
        setSelectedFolder(folder);
        setShowDeleteModal(true);
    };

    const getFilteredFolders = () => {
        let filtered = [...folders];

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(folder =>
                folder.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (folder.description && folder.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Apply department filter
        if (filterDepartment !== 'all') {
            filtered = filtered.filter(folder => folder.department_id === filterDepartment);
        }

        // Apply status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(folder => {
                if (filterStatus === 'active') return folder.is_active !== false;
                if (filterStatus === 'inactive') return folder.is_active === false;
                return true;
            });
        }

        return filtered.sort((a, b) => {
            let aValue = a[sortField] || '';
            let bValue = b[sortField] || '';
            
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    const getDepartmentName = (departmentId) => {
        const dept = departments.find(d => d.department_id === departmentId);
        return dept ? dept.name : 'No Department';
    };

    const getFormCountForFolder = (folderId) => {
        return forms.filter(form => form.folder_id === folderId).length;
    };

    const getSubfolderCount = (folderId) => {
        return folders.filter(folder => folder.parent_folder_id === folderId).length;
    };

    const toggleFolderExpansion = (folderId) => {
        setExpandedFolders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(folderId)) {
                newSet.delete(folderId);
            } else {
                newSet.add(folderId);
            }
            return newSet;
        });
    };

    if (loading) {
        return (
            <Container className="department-forms-list">
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" size="lg" />
                    <h5 className="mt-3">Loading Department Forms...</h5>
                    <p className="text-muted">Please wait while we fetch your folders and forms.</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="department-forms-list">
                <Alert variant="danger" className="m-4">
                    <Alert.Heading>Error Loading Data</Alert.Heading>
                    <p>{error}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button variant="outline-danger" onClick={initializeData}>
                            Try Again
                        </Button>
                    </div>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="department-forms-list">
            {/* Header Section */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="mb-1">
                                <FaFolder className="me-2 text-primary" />
                                Department Forms Management
                            </h3>
                            <p className="text-muted mb-0">
                                Organize and manage forms across departments with advanced folder structures
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-secondary"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <FaSync className={`me-1 ${refreshing ? 'fa-spin' : ''}`} />
                                Refresh
                            </Button>
                            <Button
                                variant="primary"
                                onClick={openCreateFolderModal}
                            >
                                <FaPlus className="me-1" />
                                Create Folder
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Quick Statistics */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="stat-card">
                        <Card.Body className="text-center">
                            <FaFolder className="stat-icon text-primary mb-2" />
                            <h4 className="text-primary mb-1">{statistics.totalFolders}</h4>
                            <small className="text-muted">Total Folders</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card">
                        <Card.Body className="text-center">
                            <FaFileAlt className="stat-icon text-success mb-2" />
                            <h4 className="text-success mb-1">{statistics.totalForms}</h4>
                            <small className="text-muted">Total Forms</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card">
                        <Card.Body className="text-center">
                            <FaBuilding className="stat-icon text-info mb-2" />
                            <h4 className="text-info mb-1">{departments.length}</h4>
                            <small className="text-muted">Departments</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card">
                        <Card.Body className="text-center">
                            <FaArchive className="stat-icon text-warning mb-2" />
                            <h4 className="text-warning mb-1">{statistics.formsWithoutFolder}</h4>
                            <small className="text-muted">Uncategorized</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Search and Filters */}
            <Card className="mb-4">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Search & Filters</h6>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        >
                            <FaFilter className="me-1" />
                            {showAdvancedFilters ? 'Hide' : 'Show'} Advanced
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text><FaSearch /></InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search folders and forms..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={filterDepartment}
                                onChange={(e) => setFilterDepartment(e.target.value)}
                            >
                                <option value="all">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept.department_id} value={dept.department_id}>
                                        {dept.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <ButtonGroup className="w-100">
                                <Button
                                    variant={viewMode === 'tree' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('tree')}
                                >
                                    <FaLayerGroup />
                                </Button>
                                <Button
                                    variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('list')}
                                >
                                    <FaSort />
                                </Button>
                                <Button
                                    variant={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                                    onClick={() => setViewMode('grid')}
                                >
                                    <FaEllipsisV />
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>

                    {showAdvancedFilters && (
                        <Row className="mt-3">
                            <Col md={4}>
                                <Form.Label>Sort By</Form.Label>
                                <Form.Select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                >
                                    <option value="name">Name</option>
                                    <option value="created_at">Created Date</option>
                                    <option value="updated_at">Modified Date</option>
                                </Form.Select>
                            </Col>
                            <Col md={4}>
                                <Form.Label>Sort Direction</Form.Label>
                                <ButtonGroup className="w-100">
                                    <Button
                                        variant={sortDirection === 'asc' ? 'primary' : 'outline-primary'}
                                        onClick={() => setSortDirection('asc')}
                                    >
                                        <FaArrowUp /> Ascending
                                    </Button>
                                    <Button
                                        variant={sortDirection === 'desc' ? 'primary' : 'outline-primary'}
                                        onClick={() => setSortDirection('desc')}
                                    >
                                        <FaArrowDown /> Descending
                                    </Button>
                                </ButtonGroup>
                            </Col>
                            <Col md={4}>
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Main Content Tabs */}
            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="overview">
                            <FaLayerGroup className="me-1" />
                            Overview
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="folders">
                            <FaFolder className="me-1" />
                            Folders ({getFilteredFolders().length})
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="forms">
                            <FaFileAlt className="me-1" />
                            Forms ({forms.length})
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="analytics">
                            <FaChartBar className="me-1" />
                            Analytics
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    {/* Overview Tab */}
                    <Tab.Pane eventKey="overview">
                        <Row>
                            <Col lg={8}>
                                <Card>
                                    <Card.Header>
                                        <h6 className="mb-0">Folder Structure</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <FolderTreeView 
                                            folders={getFilteredFolders()}
                                            forms={forms}
                                            departments={departments}
                                            expandedFolders={expandedFolders}
                                            onToggleExpansion={toggleFolderExpansion}
                                            onEditFolder={openEditFolderModal}
                                            onDeleteFolder={openDeleteModal}
                                            viewMode={viewMode}
                                        />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={4}>
                                <FolderStatistics 
                                    statistics={statistics}
                                    departments={departments}
                                    folders={folders}
                                    forms={forms}
                                />
                            </Col>
                        </Row>
                    </Tab.Pane>

                    {/* Folders Tab */}
                    <Tab.Pane eventKey="folders">
                        <Card>
                            <Card.Header>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">Folder Management</h6>
                                    <div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => setShowAssignmentWizard(true)}
                                        >
                                            <FaArrowsAlt className="me-1" />
                                            Bulk Operations
                                        </Button>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={openCreateFolderModal}
                                        >
                                            <FaPlus className="me-1" />
                                            Add Folder
                                        </Button>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {getFilteredFolders().length > 0 ? (
                                    <Table hover responsive>
                                        <thead>
                                            <tr>
                                                <th>Folder</th>
                                                <th>Department</th>
                                                <th>Forms</th>
                                                <th>Subfolders</th>
                                                <th>Created</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getFilteredFolders().map(folder => (
                                                <tr key={folder.folder_id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            {folder.parent_folder_id ? (
                                                                <FaFolder className="me-2 text-secondary" />
                                                            ) : (
                                                                <FaFolderOpen className="me-2 text-primary" />
                                                            )}
                                                            <div>
                                                                <strong>{folder.name}</strong>
                                                                {folder.description && (
                                                                    <div className="small text-muted">
                                                                        {folder.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg="info">
                                                            {getDepartmentName(folder.department_id)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={getFormCountForFolder(folder.folder_id) > 0 ? 'primary' : 'secondary'}>
                                                            <FaFileAlt className="me-1" />
                                                            {getFormCountForFolder(folder.folder_id)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={getSubfolderCount(folder.folder_id) > 0 ? 'info' : 'secondary'}>
                                                            <FaFolder className="me-1" />
                                                            {getSubfolderCount(folder.folder_id)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {new Date(folder.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <Badge bg={folder.is_active !== false ? 'success' : 'danger'}>
                                                            {folder.is_active !== false ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Dropdown>
                                                            <Dropdown.Toggle
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                id={`dropdown-${folder.folder_id}`}
                                                            >
                                                                <FaEllipsisV />
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item onClick={() => openEditFolderModal(folder)}>
                                                                    <FaEdit className="me-2" />
                                                                    Edit Folder
                                                                </Dropdown.Item>
                                                                <Dropdown.Item onClick={() => setShowAssignmentWizard(true)}>
                                                                    <FaArrowsAlt className="me-2" />
                                                                    Manage Forms
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item
                                                                    className="text-danger"
                                                                    onClick={() => openDeleteModal(folder)}
                                                                >
                                                                    <FaTrash className="me-2" />
                                                                    Delete Folder
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <div className="text-center p-5">
                                        <FaFolder size={48} className="text-muted mb-3" />
                                        <h5 className="text-muted">No folders found</h5>
                                        <p className="text-muted">Create your first folder to organize forms by department.</p>
                                        <Button variant="primary" onClick={openCreateFolderModal}>
                                            <FaPlus className="me-1" />
                                            Create Folder
                                        </Button>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    {/* Forms Tab */}
                    <Tab.Pane eventKey="forms">
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">Form Management</h6>
                            </Card.Header>
                            <Card.Body>
                                <Alert variant="info">
                                    <FaFileAlt className="me-2" />
                                    Form management features will be integrated here. This includes form assignment to folders, bulk operations, and form analytics.
                                </Alert>
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    {/* Analytics Tab */}
                    <Tab.Pane eventKey="analytics">
                        <Row>
                            <Col>
                                <Card>
                                    <Card.Header>
                                        <h6 className="mb-0">Department Analytics</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <Alert variant="info">
                                            <FaChartBar className="me-2" />
                                            Advanced analytics and reporting features will be implemented here.
                                        </Alert>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* Create Folder Modal */}
            <Modal show={showCreateFolderModal} onHide={() => setShowCreateFolderModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Create New Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Folder Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter folder name"
                                        value={folderForm.name}
                                        onChange={(e) => setFolderForm({...folderForm, name: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Department</Form.Label>
                                    <Form.Select
                                        value={folderForm.department_id}
                                        onChange={(e) => setFolderForm({...folderForm, department_id: e.target.value})}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map(dept => (
                                            <option key={dept.department_id} value={dept.department_id}>
                                                {dept.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Enter folder description"
                                value={folderForm.description}
                                onChange={(e) => setFolderForm({...folderForm, description: e.target.value})}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Parent Folder</Form.Label>
                            <Form.Select
                                value={folderForm.parent_folder_id || ''}
                                onChange={(e) => setFolderForm({...folderForm, parent_folder_id: e.target.value || null})}
                            >
                                <option value="">Root Level</option>
                                {folders.map(folder => (
                                    <option key={folder.folder_id} value={folder.folder_id}>
                                        {folder.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateFolderModal(false)}>
                        <FaTimes className="me-1" />
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateFolder}>
                        <FaSave className="me-1" />
                        Create Folder
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Folder Modal */}
            <Modal show={showEditFolderModal} onHide={() => setShowEditFolderModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Folder Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter folder name"
                                        value={folderForm.name}
                                        onChange={(e) => setFolderForm({...folderForm, name: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Department</Form.Label>
                                    <Form.Select
                                        value={folderForm.department_id || ''}
                                        onChange={(e) => {
                                            const newValue = e.target.value || ''; // Keep as empty string, not null
                                            console.log('🔄 Department selection changed:', { 
                                                from: folderForm.department_id, 
                                                to: newValue,
                                                eventValue: e.target.value,
                                                valueType: typeof newValue
                                            });
                                            setFolderForm(prevForm => ({
                                                ...prevForm, 
                                                department_id: newValue
                                            }));
                                        }}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map(dept => (
                                            <option key={dept.department_id} value={String(dept.department_id)}>
                                                {dept.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Enter folder description"
                                value={folderForm.description}
                                onChange={(e) => setFolderForm({...folderForm, description: e.target.value})}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Parent Folder</Form.Label>
                            <Form.Select
                                value={folderForm.parent_folder_id || ''}
                                onChange={(e) => setFolderForm({...folderForm, parent_folder_id: e.target.value || null})}
                            >
                                <option value="">Root Level</option>
                                {folders
                                    .filter(folder => folder.folder_id !== selectedFolder?.folder_id)
                                    .map(folder => (
                                        <option key={folder.folder_id} value={folder.folder_id}>
                                            {folder.name}
                                        </option>
                                    ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <div className="d-flex align-items-center gap-3">
                                <Form.Check
                                    type="switch"
                                    id="folder-status-edit"
                                    label={folderForm.is_active ? "Active" : "Inactive"}
                                    checked={folderForm.is_active}
                                    onChange={(e) => setFolderForm({...folderForm, is_active: e.target.checked})}
                                />
                                <Badge bg={folderForm.is_active ? 'success' : 'danger'}>
                                    {folderForm.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            </div>
                            <Form.Text className="text-muted">
                                Inactive folders are hidden from users
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditFolderModal(false)}>
                        <FaTimes className="me-1" />
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleEditFolder}>
                        <FaSave className="me-1" />
                        Update Folder
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Folder Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <strong>Warning:</strong> You are about to delete the folder "{selectedFolder?.name}".
                    </Alert>
                    <p>
                        This folder contains {getFormCountForFolder(selectedFolder?.folder_id)} forms 
                        and {getSubfolderCount(selectedFolder?.folder_id)} subfolders.
                    </p>
                    <p>
                        Forms will be moved to "Uncategorized" and subfolders will become root folders.
                    </p>
                    <p><strong>This action cannot be undone.</strong></p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        <FaTimes className="me-1" />
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteFolder}>
                        <FaTrash className="me-1" />
                        Delete Folder
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Form Assignment Wizard */}
            {showAssignmentWizard && (
                <FormAssignmentWizard
                    show={showAssignmentWizard}
                    onHide={() => setShowAssignmentWizard(false)}
                    folders={folders}
                    forms={forms}
                    onFormsAssigned={handleRefresh}
                />
            )}
        </Container>
    );
};

export default DepartmentFormsList;